import React, { useState } from 'react';
import { useTheme } from '@theme/useTheme';

interface CustomSelectProps {
    value: number | null;
    onChange: (value: number) => void;
    options: Array<{ value: number; label: string }>;
    placeholder?: string;
    hasError?: boolean;
    errorMessage?:string;
    required?: boolean;
    label?: string;
    description?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    hasError = false,
    required = false,
    label,
    description,
    errorMessage,
}) => {
    const { colors } = useTheme();
    const [isFocused, setIsFocused] = useState(false);

    const getBorderColor = () => {
        if (hasError) return colors.error;
        if (isFocused || (value && value !== 0)) return colors.primary;
        return colors.border;
    };

    const getTextColor = () => {
        if (value && value !== 0) return colors.text;
        return colors.text + '80';
    };

    return (
        <div className="w-full">
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                    {label}
                    {required && <span className="ml-1" style={{ color: colors.error }}>*</span>}
                </label>
            )}

            {/* Select Field */}
            <div className="relative">
                <select
                    value={value ?? 0}
                    onChange={(e) => onChange(parseInt(e.target.value, 10))}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="w-full px-3 py-2 rounded-lg border-2 transition-colors duration-200 appearance-none cursor-pointer focus:outline-none text-sm"
                    style={{
                        backgroundColor: colors.input,
                        borderColor: getBorderColor(),
                        color: getTextColor(),
                        minHeight: '44px',
                        paddingRight: '2.5rem',
                    }}
                >
                    <option value={0} disabled style={{ color: colors.text + '80' }}>
                        {placeholder}
                    </option>
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            style={{
                                color: colors.text,
                                backgroundColor: colors.background,
                            }}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Dropdown Arrow Icon */}
                <div
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center justify-center"
                    style={{ color: isFocused ? colors.primary : colors.text }}
                >
                    <svg
                        className="w-5 h-5 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            transform: isFocused ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </div>
            </div>

            {/* Description */}
            {!hasError && description && (
                <p className="mt-1 text-xs" style={{ color: colors.text }}>
                    {description}
                </p>
            )}

            {/* Error Message */}
            {hasError && (
                <p className="mt-1 text-xs" style={{ color: colors.error }}>
                {errorMessage || "This field is required"}
                </p>
            )}
        </div>
    );
};

export default CustomSelect;

